<template>
    <button type="button" @click.prevent.stop="toggleFavourite" class="favourite">
        <Tooltip :text="tooltipText">
            <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
                :fill="checked ? '#fce428' : 'none'"
            >
                <path
                    d="M10.0002 2.65995L12.5315 7.00603L12.6424 7.19642L12.8578 7.24305L17.7734 8.30751L14.4222 12.058L14.2754 12.2223L14.2976 12.4415L14.8043 17.4454L10.2018 15.4173L10.0002 15.3284L9.79854 15.4173L5.19606 17.4454L5.70271 12.4415L5.7249 12.2223L5.5781 12.058L2.22697 8.30751L7.14257 7.24305L7.35791 7.19642L7.4688 7.00603L10.0002 2.65995Z"
                    :stroke="checked ? '#8B7D16' : 'currentColor'"
                />
            </svg>
        </Tooltip>
    </button>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useFavouritesStore } from "@/shared/stores/use-favourites-store";
import Tooltip from "@/shared/components/atoms/Tooltip.vue";
import analytics from "@/shared/analytics";
import useSazkaSnackbar from "@/shared/composables/use-sazka-snackbar";
import { PATHS } from "@/router/paths";
import { PrematchStaticCategories } from "@/prematch/model/enums/prematch-static-categories";
import { useRouter } from "vue-router";
import { usePrematchFavouritesStore } from "@/prematch/stores/use-prematch-favourites";
import { useLiveSportsStore } from "@/live/stores/live-sports";

const router = useRouter();

const props = withDefaults(
    defineProps<{
        id: string;
        name?: string;
        type?: "sport" | "league" | "event" | "market";
    }>(),
    {
        type: "event",
    },
);

const {
    toggleFavouriteSport,
    toggleFavouriteLeague,
    toggleFavouriteEvent,
    toggleFavouriteMarket,
    isFavouriteSport,
    isFavouriteLeague,
    isFavouriteEvent,
    isFavouriteMarket,
} = useFavouritesStore();

const checked = computed(() => {
    let retVal = false;
    switch (props.type) {
        case "sport":
            retVal = isFavouriteSport(props.id);
            break;
        case "league":
            retVal = isFavouriteLeague(props.id);
            break;
        case "event":
            retVal = isFavouriteEvent(props.id);
            break;
        case "market":
            retVal = isFavouriteMarket(props.id);
            break;
    }
    return retVal;
});

const toggleFavourite = () => {
    switch (props.type) {
        case "sport":
            toggleFavouriteSport(props.id);
            break;
        case "league":
            toggleFavouriteLeague(props.id);

            if (checked.value) {
                useSazkaSnackbar().createSnackbar({
                    message: "Přidali jste ligu do oblíbených",
                    type: "success",
                    duration: 4000,
                    button: {
                        label: "Oblíbené",
                        onClick: () => {
                            if (
                                router.currentRoute.value.name === "Live" ||
                                router.currentRoute.value.name === "LiveEventDetail"
                            ) {
                                if (router.currentRoute.value.name === "LiveEventDetail") router.push({ name: "Live" });
                                const { setActiveFavourites } = useLiveSportsStore();
                                setActiveFavourites();
                            }
                            // BEWARE: If we're on /kurzy/:sportAndCountryId beforeEnter handler won't be triggered, hence this workaround
                            else if (router.currentRoute.value.name === "PrematchSport") {
                                const prematchFavouritesStore = usePrematchFavouritesStore();
                                prematchFavouritesStore.activeTab = "leagues";
                                router.push(`${PATHS.PREMATCH}/${PrematchStaticCategories.FAVORITES}`);
                            } else {
                                router.push(
                                    `${PATHS.PREMATCH}/${PrematchStaticCategories.FAVORITES}?activeTab=leagues`,
                                );
                            }
                        },
                    },
                });
            }
            break;
        case "event":
            toggleFavouriteEvent(props.id);
            if (checked.value) {
                useSazkaSnackbar().createSnackbar({
                    message: "Přidali jste zápas do oblíbených",
                    type: "success",
                    duration: 4000,
                    button: {
                        label: "Oblíbené",
                        onClick: () => {
                            if (
                                router.currentRoute.value.name === "Live" ||
                                router.currentRoute.value.name === "LiveEventDetail"
                            ) {
                                if (router.currentRoute.value.name === "LiveEventDetail") router.push({ name: "Live" });
                                const { setActiveFavourites } = useLiveSportsStore();
                                setActiveFavourites();
                            }
                            // BEWARE: If we're on /kurzy/:sportAndCountryId beforeEnter handler won't be triggered, hence this workaround
                            else if (router.currentRoute.value.name === "PrematchSport") {
                                const prematchFavouritesStore = usePrematchFavouritesStore();
                                prematchFavouritesStore.activeTab = "events";
                                router.push(`${PATHS.PREMATCH}/${PrematchStaticCategories.FAVORITES}`);
                            } else {
                                router.push(`${PATHS.PREMATCH}/${PrematchStaticCategories.FAVORITES}?activeTab=events`);
                            }
                        },
                    },
                });
            }
            break;
        case "market":
            toggleFavouriteMarket(props.id);
            break;
    }

    if (checked.value) {
        analytics.sendAddToFavorites({
            eventProperty1: translateTypeToAnalytics(props.type),
            eventProperty2: props.name,
        });
    }
};

const translateTypeToAnalytics = (type: string) => {
    switch (type) {
        case "sport":
            return "sport";
        case "league":
            return "league";
        case "event":
            return "match";
        case "market":
            return "bet";
    }
};

const tooltipText = computed(() => {
    if (props.type != "market") {
        return checked.value ? "Odebrat z oblíbených" : "Přidat do oblíbených";
    } else {
        return checked.value ? "Odepnout ze začátku seznamu" : "Připnout na začátek seznamu";
    }
});
</script>
